import { FC, useState } from 'react';
import { Button } from '@cluey/cluey-components';
import { EmailOtpChallenge, EmailOtpVerificationError } from '../../../../services/emailOtpAuth';
import LoginSubmitButton from './LoginSubmitButton';
import OtpCodeInput from './OtpCodeInput';
import ResendCode from './ResendCode';
import ResendCodeSentMessage from './ResendCodeSentMessage';
import mapOtcErrorMessages from './mapOtcErrorMessages';

interface LoginCodeStepProps {
  challenge: EmailOtpChallenge;
  onChallengeChange: (challenge: EmailOtpChallenge) => void;
  onVerify: (challenge: EmailOtpChallenge, code: string) => Promise<void>;
  onResend: () => Promise<EmailOtpChallenge>;
  onChangeEmail: () => void;
  onSignInWithPassword: () => void;
}

const LoginCodeStep: FC<LoginCodeStepProps> = ({
  challenge,
  onChallengeChange,
  onVerify,
  onResend,
  onChangeEmail,
  onSignInWithPassword,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | false>(false);
  const [resendMessageVisible, setResendMessageVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onError = (errorResponse: string) => {
    setError(mapOtcErrorMessages(errorResponse));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onVerify(challenge, code);
    } catch (e) {
      onError(e instanceof EmailOtpVerificationError ? e.reason : 'UNKNOWN');
    } finally {
      setSubmitting(false);
    }
  };

  const onResendCode = async () => {
    setCode('');
    setError(false);
    try {
      const newChallenge = await onResend();
      onChallengeChange(newChallenge);
      setResendMessageVisible(true);
    } catch {
      setError('Failed to resend the code. Please try again.');
    }
  };

  const onCodeChange = (value: string) => {
    setCode(value);
    setResendMessageVisible(false);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex w-full flex-col items-start gap-5"
    >
      <h1 className="max-w-md font-display text-[28px] leading-10 sm:text-[32px] mb-7">Enter your code</h1>

      <p className="text-grey-600">If an account matches that email address, we've sent an 8-digit code. It expires in 10 minutes. Enter it below to sign in.
        <span className="ml-1 flex-shrink-0 link" role="button" onClick={onChangeEmail}>
          Use a different email
        </span>
      </p>

      <div className="w-full mb-5">
        <OtpCodeInput error={error} value={code} onChange={onCodeChange} name="code" disabled={submitting} />
        <div className="mt-3 text-sm text-grey-5">
          <ResendCode onClick={onResendCode} />
        </div>
        {resendMessageVisible && (
          <div className="mt-3">
            <ResendCodeSentMessage onDismiss={() => setResendMessageVisible(false)} />
          </div>
        )}
      </div>
      <LoginSubmitButton loading={submitting} label="Continue" disabled={!code || submitting} />
      <div className="w-full">
        <Button size="lg" appearance="reverse" onClick={onSignInWithPassword} className="flex w-full max-w-sm flex-row justify-center !border-none !text-black">
          <span className="!text-black !font-medium">
            Use password instead
          </span>
        </Button>
      </div>
    </form>
  );
};

export default LoginCodeStep;
